import { forwardRef, useCallback } from 'react';

import { textVariants, resolveColor } from './variants';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'components/ui/tooltip';
import useHasTextOverflow from 'hooks/useHasTextOverflow';
import { cn } from 'lib/utils';

import type { TextProps, TextSize } from './types';

/**
 * Text Component
 *
 * Modern text component using shadcn/ui + Tailwind CSS.
 * Maintains 100% API compatibility with the original styled-components implementation.
 *
 * @component
 * @example
 * ```tsx
 * // Basic usage
 * <Text size="large" color="primary">Hello World</Text>
 *
 * // Truncated with auto-tooltip
 * <Text truncate maxWidth="200px">
 *   Very long text that will be truncated...
 * </Text>
 *
 * // Custom styling
 * <Text
 *   bold
 *   color="var(--custom-color)"
 *   textAlign="center"
 * >
 *   Custom styled text
 * </Text>
 * ```
 */
export const Text = forwardRef<HTMLSpanElement, TextProps>(
  (
    {
      children,
      size = 'medium',
      color = 'white',
      bold = false,
      truncate = false,
      tooltip,
      isTooltip = false,
      underline = false,
      textAlign = 'left',
      className,
      style,
      onClick,
      fontWeight,
      maxWidth = '100%',
      whiteSpace,
      secondary = false,
      flex,
      opacity = 1,
      id,
      'data-testid': dataTestId,
      ...rest
    },
    ref
  ) => {
    // Detect text overflow for auto-tooltip functionality
    const { ref: overflowRef, hasOverflow } = useHasTextOverflow(children);

    // Merge forwarded ref with overflow detection ref
    const mergedRef = useCallback(
      (node: HTMLSpanElement | null) => {
        // Set the overflow detection ref
        if (overflowRef) {
          (overflowRef as React.MutableRefObject<HTMLSpanElement | null>).current = node;
        }
        // Set the forwarded ref
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          (ref as React.MutableRefObject<HTMLSpanElement | null>).current = node;
        }
      },
      [ref, overflowRef]
    );

    // Determine if tooltip should be shown
    const shouldShowTooltip = (truncate && hasOverflow) || isTooltip;

    // Resolve color to Tailwind class or inline style
    const { className: colorClass, style: colorStyle } = resolveColor(color);

    // Build className using cva variants
    const validSizes = ['XXSmall', 'xSmall', 'small', 'medium', 'large', 'xLarge', 'XXLarge'];
    const safeSize = (validSizes.includes(size) ? size : 'medium') as TextSize;

    const variantClasses = textVariants({
      size: safeSize,
      weight: bold && !fontWeight ? 'bold' : 'normal',
      align: textAlign,
      truncate,
      underline,
      clickable: !!onClick,
      fontFamily: secondary ? 'secondary' : 'primary',
    });

    // Merge all classNames
    const mergedClassName = cn(variantClasses, colorClass, className);

    // Build merged inline styles with proper priority
    const mergedStyle: React.CSSProperties = {
      // fontWeight overrides bold prop if provided
      ...(fontWeight && { fontWeight }),
      // maxWidth: number → convert to rem, string → use as-is
      ...(maxWidth && {
        maxWidth: typeof maxWidth === 'number' ? `${maxWidth}rem` : maxWidth,
      }),
      // Pass through CSS properties
      ...(whiteSpace && { whiteSpace }),
      ...(typeof flex !== 'undefined' && { flex }),
      ...(typeof opacity !== 'undefined' && { opacity }),
      // Merge color style from resolveColor
      ...colorStyle,
      // User style prop has highest priority
      ...style,
    };

    // Add pointer cursor if onClick is provided
    const clickableStyle = onClick ? { cursor: 'pointer' } : {};
    const finalStyle = { ...mergedStyle, ...clickableStyle };

    // Render the text element with merged refs (overflow detection + forwarded ref)
    const textElement = (
      <span
        ref={mergedRef}
        id={id}
        data-testid={dataTestId}
        className={mergedClassName}
        style={finalStyle}
        onClick={onClick}
        {...rest}
      >
        {children}
      </span>
    );

    // Wrap in Radix UI Tooltip if needed
    const tooltipText = tooltip || (shouldShowTooltip ? children : undefined);

    if (tooltipText && typeof tooltipText === 'string') {
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>{textElement}</TooltipTrigger>
            <TooltipContent>
              <p>{tooltipText}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    return textElement;
  }
);

Text.displayName = 'Text';

// Re-export types for convenient importing
export type { TextProps, TextSize, TextColor, TextAlign } from './types';
export type { TextVariants } from './variants';

export default Text;
